import { memo, useRef, useState, useEffect } from 'react';
import { Statistic } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { ChartData, StatData } from '@/types/query';

export const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

// Format axis values based on format type
export const formatXAxis = (value: any, chartData: ChartData) => {
  if (!chartData.xAxisFormat || chartData.xAxisFormat === 'string') return value;
  
  if (chartData.xAxisFormat === 'date') {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  
  if (chartData.xAxisFormat === 'month') {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  
  if (chartData.xAxisFormat === 'datetime') {
    const date = new Date(value);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  
  return value;
};

export const formatYAxis = (value: any, chartData: ChartData) => {
  if (!chartData.yAxisFormat || chartData.yAxisFormat === 'decimal') {
    return typeof value === 'number' ? value.toFixed(2) : value;
  }
  
  if (chartData.yAxisFormat === 'integer') {
    return Math.round(value);
  }
  
  if (chartData.yAxisFormat === 'currency') {
    return `$${typeof value === 'number' ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}`;
  }
  
  if (chartData.yAxisFormat === 'percentage') {
    return `${value}%`;
  }
  
  return value;
};

// Color palette for multi-series charts
const CHART_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

// Helper to get series label - uses AI-provided label if available, else auto-generates from column name
const getSeriesLabel = (yKey: string, index: number, chartData: ChartData): string => {
  // Use AI-provided label if available
  if (chartData.yLabels && chartData.yLabels[index]) {
    return chartData.yLabels[index];
  }
  // Fall back to auto-generated label from column name
  return yKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Builds the inner chart JSX (no ResponsiveContainer wrapper)
function buildChart(chartData: ChartData) {
  // Handle multi-series - use parsed yKeys if available, else parse from yKey string
  const yKeys = chartData.yKeys || (chartData.yKey?.includes(',') 
    ? chartData.yKey.split(',').map(k => k.trim()) 
    : chartData.yKey ? [chartData.yKey] : []);
  
  if (chartData.type === 'line') {
    return (
      <LineChart data={chartData.data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={chartData.xKey}
          tickFormatter={(value) => formatXAxis(value, chartData)}
          label={chartData.xAxisLabel ? { value: chartData.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
        />
        <YAxis 
          tickFormatter={(value) => formatYAxis(value, chartData)}
          label={chartData.yAxisLabel ? { value: chartData.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
        />
        <RechartsTooltip 
          labelFormatter={(value) => formatXAxis(value, chartData)}
          formatter={(value: any) => [formatYAxis(value, chartData)]}
        />
        <Legend />
        {yKeys.map((yKey, index) => (
          <Line 
            key={yKey}
            type="monotone" 
            dataKey={yKey}
            name={getSeriesLabel(yKey, index, chartData)}
            stroke={CHART_COLORS[index % CHART_COLORS.length]} 
            strokeWidth={2} 
            dot={{ r: 4 }} 
          />
        ))}
      </LineChart>
    );
  }

  if (chartData.type === 'bar') {
    const isHorizontal = chartData.orientation === 'horizontal';
    
    return (
      <BarChart data={chartData.data} layout={isHorizontal ? 'vertical' : 'horizontal'}>
        <CartesianGrid strokeDasharray="3 3" />
        {isHorizontal ? (
          <>
            <XAxis 
              type="number"
              tickFormatter={(value) => formatYAxis(value, chartData)}
              label={chartData.yAxisLabel ? { value: chartData.yAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
            />
            <YAxis 
              type="category"
              dataKey={chartData.xKey}
              tickFormatter={(value) => formatXAxis(value, chartData)}
              label={chartData.xAxisLabel ? { value: chartData.xAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
          </>
        ) : (
          <>
            <XAxis 
              dataKey={chartData.xKey}
              tickFormatter={(value) => formatXAxis(value, chartData)}
              label={chartData.xAxisLabel ? { value: chartData.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
            />
            <YAxis 
              tickFormatter={(value) => formatYAxis(value, chartData)}
              label={chartData.yAxisLabel ? { value: chartData.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
          </>
        )}
        <RechartsTooltip 
          labelFormatter={(value) => formatXAxis(value, chartData)}
          formatter={(value: any) => [formatYAxis(value, chartData)]}
        />
        <Legend />
        {yKeys.map((yKey, index) => (
          <Bar 
            key={yKey}
            dataKey={yKey} 
            name={getSeriesLabel(yKey, index, chartData)} 
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            stackId={chartData.stacked ? 'stack' : undefined}
          />
        ))}
      </BarChart>
    );
  }

  if (chartData.type === 'area') {
    return (
      <AreaChart data={chartData.data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={chartData.xKey}
          tickFormatter={(value) => formatXAxis(value, chartData)}
          label={chartData.xAxisLabel ? { value: chartData.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
        />
        <YAxis 
          tickFormatter={(value) => formatYAxis(value, chartData)}
          label={chartData.yAxisLabel ? { value: chartData.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
        />
        <RechartsTooltip 
          labelFormatter={(value) => formatXAxis(value, chartData)}
          formatter={(value: any) => [formatYAxis(value, chartData)]}
        />
        <Legend />
        {yKeys.map((yKey, index) => (
          <Area 
            key={yKey}
            type="monotone" 
            dataKey={yKey}
            name={getSeriesLabel(yKey, index, chartData)}
            stroke={CHART_COLORS[index % CHART_COLORS.length]} 
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            fillOpacity={chartData.stacked ? 0.8 : 0.6}
            stackId={chartData.stacked ? 'stack' : undefined}
          />
        ))}
      </AreaChart>
    );
  }

  if (chartData.type === 'pie') {
    return (
      <PieChart>
        <Pie
          data={chartData.data}
          dataKey={chartData.yKey}
          nameKey={chartData.xKey}
          cx="50%"
          cy="50%"
          outerRadius={100}
          label={(entry: any) => formatYAxis(entry[chartData.yKey], chartData)}
        >
          {chartData.data.map((_entry: any, index: number) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <RechartsTooltip formatter={(value: any) => [formatYAxis(value, chartData), chartData.yLabel || chartData.yKey]} />
        <Legend />
      </PieChart>
    );
  }

  if (chartData.type === 'scatter') {
    return (
      <ScatterChart>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={chartData.xKey} 
          type="number" 
          name={chartData.xLabel || chartData.xKey}
          tickFormatter={(value) => formatXAxis(value, chartData)}
        />
        <YAxis 
          type="number" 
          name={chartData.yLabel || 'Value'}
          tickFormatter={(value) => formatYAxis(value, chartData)}
        />
        <RechartsTooltip 
          formatter={(value: any) => [formatYAxis(value, chartData)]}
        />
        <Legend />
        {yKeys.map((yKey, index) => (
          <Scatter 
            key={yKey}
            name={getSeriesLabel(yKey, index, chartData)} 
            data={chartData.data} 
            dataKey={yKey}
            fill={CHART_COLORS[index % CHART_COLORS.length]} 
          />
        ))}
      </ScatterChart>
    );
  }

  if (chartData.type === 'radar') {
    return (
      <RadarChart data={chartData.data}>
        <PolarGrid />
        <PolarAngleAxis dataKey={chartData.xKey} />
        <PolarRadiusAxis tickFormatter={(value) => formatYAxis(value, chartData)} />
        <RechartsTooltip formatter={(value: any) => [formatYAxis(value, chartData)]} />
        <Legend />
        {yKeys.map((yKey, index) => (
          <Radar 
            key={yKey}
            name={getSeriesLabel(yKey, index, chartData)}
            dataKey={yKey} 
            stroke={CHART_COLORS[index % CHART_COLORS.length]} 
            fill={CHART_COLORS[index % CHART_COLORS.length]} 
            fillOpacity={0.6} 
          />
        ))}
      </RadarChart>
    );
  }

  if (chartData.type === 'composed') {
    // Flexible composed chart - uses seriesTypes to specify chart type for each series
    // Falls back to legacy bar+line behavior if seriesTypes not provided
    const seriesTypes = chartData.seriesTypes || ['bar', 'line']; // Default to bar for yKey, line for yKey2
    
    return (
      <ComposedChart data={chartData.data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={chartData.xKey}
          tickFormatter={(value) => formatXAxis(value, chartData)}
          label={chartData.xAxisLabel ? { value: chartData.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
        />
        <YAxis 
          tickFormatter={(value) => formatYAxis(value, chartData)}
          label={chartData.yAxisLabel ? { value: chartData.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
        />
        <RechartsTooltip 
          labelFormatter={(value) => formatXAxis(value, chartData)}
          formatter={(value: any, name: any) => [formatYAxis(value, chartData), name]}
        />
        <Legend />
        {yKeys.map((yKey, index) => {
          const seriesType = seriesTypes[index] || 'bar';
          const color = CHART_COLORS[index % CHART_COLORS.length];
          const name = getSeriesLabel(yKey, index, chartData);
          
          if (seriesType === 'bar') {
            return <Bar key={yKey} dataKey={yKey} name={name} fill={color} />;
          } else if (seriesType === 'line') {
            return <Line key={yKey} type="monotone" dataKey={yKey} name={name} stroke={color} strokeWidth={2} />;
          } else if (seriesType === 'area') {
            return <Area key={yKey} type="monotone" dataKey={yKey} name={name} stroke={color} fill={color} fillOpacity={0.6} />;
          }
          return null;
        })}
      </ComposedChart>
    );
  }

  if (chartData.type === 'table') {
    return <TableRenderer data={chartData.data} chartData={chartData} />;
  }

  return null;
}

interface TableRendererProps {
  data: Array<{ [key: string]: string | number }>;
  chartData?: ChartData; // Optional: for formatting hints (xKey, yLabels, xAxisFormat, yAxisFormat)
}

const TableRendererComponent = ({ data, chartData }: TableRendererProps) => {
  if (!data || data.length === 0) {
    return <div className="text-gray-500 text-center p-4">No data to display</div>;
  }

  // Ensure xKey is first, then yKeys in order, then any remaining columns
  const rawColumns = Object.keys(data[0]);
  const xKey = chartData?.xKey;
  const yKeys = chartData?.yKeys || (chartData?.yKey?.includes(',')
    ? chartData.yKey.split(',').map(k => k.trim())
    : chartData?.yKey ? [chartData.yKey] : []);
  const orderedColumns = [
    ...(xKey && rawColumns.includes(xKey) ? [xKey] : []),
    ...yKeys.filter(k => rawColumns.includes(k)),
    ...rawColumns.filter(k => k !== xKey && !yKeys.includes(k))
  ];
  const columns = orderedColumns.length > 0 ? orderedColumns : rawColumns;
  const maxRows = 50;
  const displayData = data.slice(0, maxRows);
  const hasMore = data.length > maxRows;

  const getColumnHeader = (col: string) => {
    if (chartData) {
      // x column: use xLabel if provided
      if (col === chartData.xKey && chartData.xLabel) return chartData.xLabel;
      // y columns: find the matching yKey and use its yLabel
      if (chartData.yLabels) {
        const yKeys = chartData.yKeys || (chartData.yKey?.includes(',')
          ? chartData.yKey.split(',').map(k => k.trim())
          : chartData.yKey ? [chartData.yKey] : []);
        const yIndex = yKeys.indexOf(col);
        if (yIndex !== -1 && chartData.yLabels[yIndex]) return chartData.yLabels[yIndex];
      }
    }
    return col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatCell = (col: string, value: any) => {
    if (!chartData) return value ?? '';
    if (col === chartData.xKey) return formatXAxis(value, chartData);
    return formatYAxis(value, chartData);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg text-xs">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider"
              >
                {getColumnHeader(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {displayData.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {columns.map((col) => (
                <td key={col} className="px-3 py-1.5 whitespace-nowrap text-gray-800">
                  {formatCell(col, row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="text-center text-xs text-gray-400 mt-1.5">
          Showing {maxRows} of {data.length} rows
        </div>
      )}
    </div>
  );
};

export const TableRenderer = memo(TableRendererComponent);

interface ChartRendererProps {
  chartData: ChartData;
  height: number | string;
}

/**
 * ChartRenderer for normal use (Explore page, modals) - uses ResponsiveContainer.
 */
const ChartRendererComponent = ({ chartData, height }: ChartRendererProps) => {
  if (!chartData || !chartData.data || chartData.data.length === 0) return null;

  const chart = buildChart(chartData);
  if (!chart) return null;

  return (
    <ResponsiveContainer width="100%" height={height as number}>
      {chart}
    </ResponsiveContainer>
  );
};

export const ChartRenderer = memo(ChartRendererComponent);

/**
 * DashboardChartRenderer for dashboard widgets - measures container once and renders 
 * chart at fixed pixel dimensions. Avoids ResponsiveContainer's ResizeObserver which
 * causes massive memory leaks during react-grid-layout drag/resize operations.
 */
const DashboardChartRendererComponent = ({ chartData }: { chartData: ChartData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Measure once on mount
    const rect = containerRef.current.getBoundingClientRect();
    setDimensions({ width: Math.floor(rect.width), height: Math.floor(rect.height) });

    // Only re-measure on actual window resize, not on drag transforms
    const handleResize = () => {
      if (containerRef.current) {
        const r = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.floor(r.width), height: Math.floor(r.height) });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!chartData || !chartData.data || chartData.data.length === 0) return null;

  const chart = buildChart(chartData);
  if (!chart) return null;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
      {dimensions ? (
        <ResponsiveContainer width={dimensions.width} height={dimensions.height as number}>
          {chart}
        </ResponsiveContainer>
      ) : null}
    </div>
  );
};

export const DashboardChartRenderer = memo(DashboardChartRendererComponent);

interface StatCardRendererProps {
  statData: StatData;
}

const StatCardRendererComponent = ({ statData }: StatCardRendererProps) => (
  <div className="mt-4 p-6 bg-white border border-gray-200 rounded-xl">
    <div className="flex justify-center">
      <Statistic
        title={statData.label}
        value={statData.value}
        precision={typeof statData.value === 'number' ? 0 : undefined}
        valueStyle={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1890ff', textAlign: 'center' }}
        className="text-center"
        prefix={
          statData.trend?.direction === 'up' ? (
            <ArrowUpOutlined style={{ color: '#52c41a' }} />
          ) : statData.trend?.direction === 'down' ? (
            <ArrowDownOutlined style={{ color: '#ff4d4f' }} />
          ) : undefined
        }
        suffix={statData.trend ? `${statData.trend.percentage}%` : undefined}
      />
    </div>
    {(statData.comparisonLabel || statData.comparisonValue !== undefined) && (
      <div className="mt-3 text-center text-sm text-gray-500">
        {statData.comparisonValue !== undefined && (
          <span className="font-medium">{statData.comparisonValue} </span>
        )}
        {statData.comparisonLabel && <span>{statData.comparisonLabel}</span>}
      </div>
    )}
  </div>
);

export const StatCardRenderer = memo(StatCardRendererComponent);
